/**
 * The Policy tab (Windows PCs): remote lock, which Windows policy this PC is held to, its last report,
 * and the BitLocker recovery keys its agent stored.
 *
 * Remote lock: "Lock screen now" (any staff, online only) sends every signed-in session to the sign-in
 * screen once. "Lock until unlocked" (admins) keeps doing so - across restarts and while offline - until
 * an admin unlocks it here.
 *
 * The agent (1.31.0+, as the installed service) changes each setting that does not match, reads again
 * and reports; each check says what it found and what the agent did. "Check now" asks it to do that
 * now. The report shown is the server's stored copy, so it is there while the PC is offline, with the
 * time it was collected. Showing a recovery password is admin-only and audited.
 */
import { insets } from 'webwin';
import * as auth from '../../api/auth.js';
import * as lockApi from '../../api/deviceLock.js';
import { describeError } from '../../api/http.js';
import * as wp from '../../api/windowsPolicy.js';
import {
  PolicyCheckState,
  PolicyEnforcement,
  type BitLockerKeyDto,
  type DeviceWindowsPolicyDto,
  type WindowsPolicyCheck,
  type WindowsPolicyDto
} from '../../contracts.gen.js';
import { join, localDateTime, plural } from '../../format.js';
import { CardList } from '../../ui/CardList.js';
import { chip } from '../../ui/Chip.js';
import { confirm } from '../../ui/dialogs.js';
import { StatusLine } from '../../ui/StatusLine.js';
import { button, buttonRow, card, comboBox, field, fillWith, FONT, GAP, h2, inlineRow, muted, splitRow, stackOf, textBox, value, vstack } from '../../ui/layout.js';
import { CHECK_STATE_LABELS, ENFORCEMENT_LABELS, checkStateTone, enforcementTone } from '../../windowsPolicy.js';
import type { DeviceTab, DeviceTabContext } from './context.js';

export function policyTab(dc: DeviceTabContext): DeviceTab {
  const { ctx } = dc;
  const isAdmin = auth.isAdmin();
  let policies: WindowsPolicyDto[] = [];
  let current: DeviceWindowsPolicyDto | null = null;
  let loadedFor = 0;
  let busy = false;
  const revealed = new Map<string, string>();

  const assignedLine = value('', { font: FONT.bold });
  const reportLine = muted('');
  const enforcementLine = muted('', 'warn');
  const staleLine = muted('', 'warn');
  const picker = comboBox<string>([], {});
  const assignButton = button('Assign', () => void assign());
  const checkButton = button('Check now', () => void check(), true);
  const status = new StatusLine();

  const checks = new CardList<WindowsPolicyCheck>({
    key: c => c.setting,
    version: c => `${c.state}|${c.actual}|${c.detail ?? ''}|${c.enforcement}|${c.enforcementDetail ?? ''}`,
    build: buildCheckCard
  });
  const emptyLine = muted('');

  const keys = new CardList<BitLockerKeyDto>({
    key: k => k.protectorId,
    version: k => `${k.escrowedUtc}|${revealed.get(k.protectorId) ?? ''}`,
    build: buildKeyCard
  });
  const keysLine = muted('');
  const keysStatus = new StatusLine();

  const lockStateLine = value('', { font: FONT.bold });
  const lockDetailLine = muted('');
  const lockErrorLine = muted('', 'danger');
  const lockMessage = textBox({ placeholder: 'e.g. Call the help desk at 555-0100' });
  const lockScreenButton = button('Lock screen now', () => void lockScreen());
  const holdButton = button('Lock until unlocked', () => void hold());
  const unlockButton = button('Unlock', () => void unlock(), true);
  const lockStatus = new StatusLine();
  holdButton.visible = isAdmin;
  unlockButton.visible = false;

  const panel = vstack({ spacing: GAP.md });
  const lockCard = card();
  lockCard.controls.add(
    stackOf(
      vstack({ spacing: GAP.md }),
      h2('Remote lock'),
      muted(
        isAdmin
          ? 'Lock screen now sends everyone signed in to the sign-in screen; their password gets them back in. Lock until unlocked keeps sending them back - after a restart and while offline too - until you unlock it here.'
          : 'Lock screen now sends everyone signed in to the sign-in screen; their password gets them back in. An administrator can also lock the PC until it is unlocked here.'
      ),
      lockStateLine,
      lockDetailLine,
      lockErrorLine,
      field('Message on the PC (optional)', lockMessage),
      stackOf(buttonRow(), lockScreenButton, holdButton, unlockButton),
      lockStatus.label
    )
  );
  const assignCard = card();
  assignCard.controls.add(
    stackOf(
      vstack({ spacing: GAP.md }),
      h2('Windows policy'),
      muted('The agent changes any setting that does not match the policy, then reports. It only tightens: a setting already stricter than the policy is left alone.'),
      assignedLine,
      field('Policy', picker),
      stackOf(buttonRow(), assignButton, checkButton),
      status.label
    )
  );
  const reportCard = card();
  reportCard.controls.add(stackOf(vstack({ spacing: GAP.md }), h2('Last report'), reportLine, enforcementLine, staleLine, emptyLine, checks.panel));
  const keysCard = card();
  keysCard.controls.add(
    stackOf(
      vstack({ spacing: GAP.md }),
      h2('BitLocker recovery keys'),
      muted('The agent stores the recovery key here before it turns BitLocker on, and for a drive that was already encrypted.'),
      keysLine,
      keys.panel,
      keysStatus.label
    )
  );
  panel.controls.addRange([lockCard, assignCard, reportCard, keysCard]);

  function buildCheckCard(c: WindowsPolicyCheck) {
    const left = vstack({ spacing: GAP.xs });
    const title = inlineRow();
    title.controls.add(value(c.label, { font: FONT.bold }));
    title.controls.add(chip(CHECK_STATE_LABELS[c.state], checkStateTone(c.state)));
    if (c.enforcement !== PolicyEnforcement.None) title.controls.add(chip(ENFORCEMENT_LABELS[c.enforcement], enforcementTone(c.enforcement)));
    left.controls.add(title);
    left.controls.add(muted(`Expected: ${c.expected} · Found: ${c.actual}`));
    if (c.detail) left.controls.add(muted(c.detail));
    if (c.enforcementDetail) left.controls.add(muted(c.enforcementDetail, c.enforcement === PolicyEnforcement.Failed ? 'danger' : 'muted'));
    const row = splitRow({ spacing: GAP.md });
    fillWith(row, left);
    const c2 = card({ padding: insets(10) });
    c2.controls.add(row);
    return c2;
  }

  function buildKeyCard(k: BitLockerKeyDto) {
    const left = vstack({ spacing: GAP.xs });
    left.controls.add(value(`${k.drive} · stored ${localDateTime(k.escrowedUtc)}`, { font: FONT.bold }));
    left.controls.add(muted(`Key protector ${k.protectorId}`));
    const password = revealed.get(k.protectorId);
    if (password) left.controls.add(value(password, { font: FONT.bold }));
    const row = splitRow({ spacing: GAP.md });
    fillWith(row, left);
    if (isAdmin && !password) row.controls.add(button('Show key', () => void reveal(k)));
    const c2 = card({ padding: insets(10) });
    c2.controls.add(row);
    return c2;
  }

  function bind(dto: DeviceWindowsPolicyDto): void {
    current = dto;
    assignedLine.text = dto.policyName ? `Held to "${dto.policyName}"` : 'No Windows policy assigned';
    picker.items = ['No policy', ...policies.map(p => (p.isDefault ? `${p.name} (default)` : p.name))];
    const index = dto.policyId === null ? 0 : policies.findIndex(p => p.id === dto.policyId) + 1;
    picker.selectedIndex = Math.max(0, index);

    const report = dto.compliance;
    const rows = report?.checks ?? [];
    const issues = rows.filter(r => r.state === PolicyCheckState.NotCompliant).length;
    const unknown = rows.filter(r => r.state === PolicyCheckState.Unknown).length;
    const changed = rows.filter(r => r.enforcement === PolicyEnforcement.Applied || r.enforcement === PolicyEnforcement.Reverted).length;
    reportLine.text = report
      ? join([
          `Collected ${localDateTime(report.collectedUtc)}`,
          issues === 0 ? (rows.length === 0 ? 'the policy checks nothing' : 'everything checked matches') : `${plural(issues, 'setting')} ${issues === 1 ? 'does' : 'do'} not match`,
          changed > 0 ? `the agent changed ${plural(changed, 'setting')}` : null,
          unknown > 0 ? `${unknown} could not be read` : null,
          report.edition ? `Windows ${report.edition}` : null,
          report.domainJoined ? 'domain-joined' : null
        ])
      : '';
    reportLine.role = issues > 0 ? 'danger' : 'muted';
    enforcementLine.text = report && !report.enforced
      ? report.enforcementNote ?? 'This agent only reports. Update it to 1.31.0 or later and it changes settings to match.'
      : '';
    enforcementLine.visible = enforcementLine.text.length > 0;
    staleLine.text = dto.stale ? 'This report was made before the policy last changed. Check now for a current one.' : '';
    staleLine.visible = dto.stale;
    emptyLine.text = !dto.policyId
      ? 'Assign a policy to hold this PC to it.'
      : report
        ? ''
        : dc.isOnline()
          ? 'No report yet. The PC applies the policy and reports with its next inventory, or press Check now.'
          : 'No report yet. The PC applies the policy the next time it comes online.';
    emptyLine.visible = emptyLine.text.length > 0;
    checks.set(rows);
    applyPresence();
    dc.relayout();
  }

  function bindKeys(list: BitLockerKeyDto[]): void {
    keysLine.text = list.length === 0 ? 'None stored. A policy that requires BitLocker makes the agent store one.' : '';
    keysLine.visible = keysLine.text.length > 0;
    keys.set(list);
    dc.relayout();
  }

  function bindLock(): void {
    const held = dc.device()?.lock ?? null;
    lockStateLine.text = held ? 'Locked until unlocked' : 'Not locked';
    lockStateLine.role = held ? 'warn' : 'text';
    lockDetailLine.text = held
      ? join([
          `Locked ${localDateTime(held.lockedUtc)}${held.lockedBy ? ` by ${held.lockedBy}` : ''}`,
          held.appliedUtc ? `the PC confirmed it ${localDateTime(held.appliedUtc)}` : dc.isOnline() ? 'waiting for the PC to confirm' : 'the PC applies it when it comes online',
          held.message ? `message: "${held.message}"` : null
        ])
      : '';
    lockDetailLine.visible = lockDetailLine.text.length > 0;
    lockErrorLine.text = held?.lastError ?? '';
    lockErrorLine.visible = lockErrorLine.text.length > 0;
    holdButton.text = held ? 'Change message' : 'Lock until unlocked';
    unlockButton.visible = isAdmin && held !== null;
  }

  function applyPresence(): void {
    checkButton.enabled = !busy && dc.isOnline() && (current?.policyId ?? null) !== null;
    assignButton.enabled = !busy;
    lockScreenButton.enabled = !busy && dc.isOnline();
    holdButton.enabled = !busy;
    unlockButton.enabled = !busy;
  }

  function messageText(): string | null {
    const text = lockMessage.text.trim();
    return text.length > 0 ? text : null;
  }

  async function lockScreen(): Promise<void> {
    const name = dc.device()?.name ?? 'this PC';
    const ok = await confirm(ctx.desktop, {
      title: 'Lock screen now',
      message: `Send everyone signed in to ${name} to the sign-in screen?\n\nTheir programs keep running, and their password gets them back in.`
    });
    if (!ok || ctx.cancelled()) return;
    busy = true;
    applyPresence();
    lockStatus.busy('Locking…');
    const result = await lockApi.lockScreen(dc.deviceId, messageText());
    if (ctx.cancelled()) return;
    busy = false;
    applyPresence();
    if (!result.success) lockStatus.error(result.error ?? 'The lock was not sent.');
    else if (result.sessionsLocked === 0) lockStatus.ok('Nobody was signed in - the PC was already at its sign-in screen.');
    else lockStatus.ok(`Locked ${plural(result.sessionsLocked, 'session')}.`);
    dc.relayout();
  }

  async function hold(): Promise<void> {
    const name = dc.device()?.name ?? 'this PC';
    const relock = (dc.device()?.lock ?? null) !== null;
    if (!relock) {
      const ok = await confirm(ctx.desktop, {
        title: 'Lock until unlocked',
        message:
          `Lock ${name} until you unlock it here?\n\nNobody can use it - anyone who signs in is sent straight back to the sign-in screen, ` +
          'after restarts and while it is offline too. Use this for a lost or stolen PC, or one that must not be used.'
      });
      if (!ok || ctx.cancelled()) return;
    }
    busy = true;
    applyPresence();
    lockStatus.busy(relock ? 'Changing the message…' : 'Locking…');
    try {
      await lockApi.holdLock(dc.deviceId, messageText());
      if (ctx.cancelled()) return;
      lockStatus.ok(relock ? 'Message changed.' : dc.isOnline() ? 'Locked. The PC applies it now.' : 'Locked. The PC applies it when it comes online.');
      await dc.reload();
    } catch (error) {
      if (!ctx.cancelled()) lockStatus.error(describeError(error));
    } finally {
      busy = false;
      applyPresence();
    }
  }

  async function unlock(): Promise<void> {
    const name = dc.device()?.name ?? 'this PC';
    const ok = await confirm(ctx.desktop, { title: 'Unlock', message: `Unlock ${name}? People can sign in and use it again.` });
    if (!ok || ctx.cancelled()) return;
    busy = true;
    applyPresence();
    lockStatus.busy('Unlocking…');
    try {
      await lockApi.releaseLock(dc.deviceId);
      if (ctx.cancelled()) return;
      lockStatus.ok(dc.isOnline() ? 'Unlocked.' : 'Unlocked. The PC lets go when it comes online.');
      await dc.reload();
    } catch (error) {
      if (!ctx.cancelled()) lockStatus.error(describeError(error));
    } finally {
      busy = false;
      applyPresence();
    }
  }

  async function load(): Promise<void> {
    const device = dc.device();
    if (!device || !dc.isWindows()) return;
    try {
      const [list, dto, recovery] = await Promise.all([
        wp.getWindowsPolicies(device.customerId, ctx.dispose.signal()),
        wp.getDeviceWindowsPolicy(dc.deviceId, ctx.dispose.signal()),
        wp.getRecoveryKeys(dc.deviceId, ctx.dispose.signal())
      ]);
      if (ctx.cancelled()) return;
      policies = list;
      bind(dto);
      bindKeys(recovery);
    } catch (error) {
      if (!ctx.cancelled()) status.error('Could not load the Windows policy: ' + describeError(error));
    }
  }

  async function assign(): Promise<void> {
    const policy = picker.selectedIndex <= 0 ? null : policies[picker.selectedIndex - 1] ?? null;
    if ((policy?.id ?? null) === (current?.policyId ?? null)) {
      status.ok('That is already this PC\'s policy.');
      return;
    }
    busy = true;
    applyPresence();
    status.busy('Saving…');
    try {
      const dto = await wp.assignWindowsPolicy(dc.deviceId, policy?.id ?? null);
      if (ctx.cancelled()) return;
      bind(dto);
      status.ok(policy ? `Assigned "${policy.name}".${dc.isOnline() ? ' The PC applies it shortly.' : ''}` : 'Took this PC off its policy.');
      await dc.reload();
    } catch (error) {
      if (!ctx.cancelled()) status.error(describeError(error));
    } finally {
      busy = false;
      applyPresence();
    }
  }

  async function check(): Promise<void> {
    busy = true;
    applyPresence();
    status.busy('Asking the PC to apply its policy and report…');
    const result = await wp.checkWindowsPolicy(dc.deviceId);
    if (ctx.cancelled()) return;
    busy = false;
    if (!result.success) {
      status.error(result.error ?? 'The check did not run.');
      applyPresence();
      return;
    }
    const changed = result.compliance?.checks.filter(c => c.enforcement === PolicyEnforcement.Applied || c.enforcement === PolicyEnforcement.Reverted).length ?? 0;
    status.ok(changed > 0 ? `Checked. The agent changed ${plural(changed, 'setting')}.` : 'Checked.');
    await load();
    await dc.reload();
  }

  async function reveal(k: BitLockerKeyDto): Promise<void> {
    keysStatus.busy('Fetching the recovery key…');
    try {
      const key = await wp.revealRecoveryKey(dc.deviceId, k.protectorId);
      if (ctx.cancelled()) return;
      revealed.set(k.protectorId, key.recoveryPassword);
      keysStatus.ok('Shown. Viewing a recovery key is recorded in the audit log.');
      keys.refresh();
      dc.relayout();
    } catch (error) {
      if (!ctx.cancelled()) keysStatus.error(describeError(error));
    }
  }

  return {
    panel,
    onShow() {
      void load();
    },
    applyDevice() {
      const device = dc.device();
      if (!device || !dc.isWindows()) return;
      bindLock();
      if (loadedFor !== device.id) {
        loadedFor = device.id;
        revealed.clear();
        void load();
      } else {
        applyPresence();
      }
      dc.relayout();
    }
  };
}
